import { useCallback, useEffect } from 'react';
import { type BlockerFunction, useBlocker } from 'react-router-dom';

import { UnsavedChangesDialog } from '../../../shared/components/unsaved-changes-dialog';

/**
 * Warn on a full-page unload (reload / tab close) while there are unsaved
 * changes. Safe to mount any number of times — inactive instances early-return
 * and add no listener. This one stays NATIVE by necessity: `beforeunload` is
 * the only hook the browser offers for tab close/reload, and it only accepts
 * the built-in prompt.
 */
function useBeforeUnloadWarning(hasUnsavedChanges: boolean) {
  useEffect(() => {
    if (!hasUnsavedChanges) return;

    const handleBeforeUnload = (event: BeforeUnloadEvent) => {
      event.preventDefault();
      event.returnValue = '';
      return '';
    };

    window.addEventListener('beforeunload', handleBeforeUnload);
    return () => {
      window.removeEventListener('beforeunload', handleBeforeUnload);
    };
  }, [hasUnsavedChanges]);
}

/**
 * Registers the React Router navigation blocker and prompts on an in-app
 * navigation away from an unsaved page.
 *
 * The prompt is the in-app {@link UnsavedChangesDialog}, not `window.confirm`:
 * the browser can suppress repeated native dialogs ("prevent this page from
 * creating additional dialogs"), and a suppressed confirm returns `false`
 * instantly — silently cancelling the navigation with no feedback. The dialog
 * renders while the blocker is in its 'blocked' state, so there is no
 * re-entry bookkeeping: Leave proceeds the blocker, Stay resets it, and the
 * state change closes the dialog.
 *
 * CRITICAL: this is only ever rendered while there ARE unsaved changes (see
 * {@link NavigationGuard}). React Router honours a SINGLE blocker at a time —
 * its `shouldBlockNavigation` evaluates only the LAST-registered blocker and
 * ignores the rest (it even warns "A router only supports one blocker at a
 * time"). If this blocker were registered unconditionally, an always-inactive
 * sibling guard mounted later would shadow it and silently let the navigation
 * through with no prompt. On assistant pages that is exactly what happens: the
 * global AI assistant mounts offscreen `PageProvider`s (headless-digest host +
 * live chart previews), each of which is an inactive guard that registers after
 * the real one. Gating the mount keeps exactly one blocker registered.
 */
function RouterNavigationBlocker({
  onDiscardChanges,
}: {
  onDiscardChanges?: () => void;
}) {
  const shouldBlock = useCallback<BlockerFunction>(
    ({ currentLocation, nextLocation }) => {
      if (nextLocation.state?.skipGuard) return false;
      if (nextLocation.pathname === currentLocation.pathname) return false;

      const normalize = (pathname: string) => pathname.replace(/\/edit$/, '');

      return (
        normalize(currentLocation.pathname) !==
          normalize(nextLocation.pathname) ||
        currentLocation.pathname.endsWith('/edit')
      );
    },
    []
  );

  const blocker = useBlocker(shouldBlock);

  return (
    <UnsavedChangesDialog
      isOpen={blocker.state === 'blocked'}
      onStay={() => {
        if (blocker.state === 'blocked') blocker.reset();
      }}
      onLeave={() => {
        if (blocker.state !== 'blocked') return;
        onDiscardChanges?.();
        blocker.proceed();
      }}
    />
  );
}

/**
 * Prompts the user when navigating away from a page with unsaved changes
 * (and warns on full-page unload).
 *
 * RENDER this — don't call it as a hook. The router blocker is only registered
 * while `hasUnsavedChanges` is true, so it never competes with the inactive
 * guards other `PageProvider`s register (React Router keeps only one blocker).
 */
export function NavigationGuard({
  hasUnsavedChanges,
  onDiscardChanges,
}: {
  hasUnsavedChanges: boolean;
  onDiscardChanges?: () => void;
}) {
  useBeforeUnloadWarning(hasUnsavedChanges);

  return hasUnsavedChanges ? (
    <RouterNavigationBlocker onDiscardChanges={onDiscardChanges} />
  ) : null;
}
